import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AuthUser {
  id: string;
  username: string;
  displayName: string;
  role: string;
  avatarSeed?: string;
  avatarType: string;
  avatarSvg: string | null;
  avatarColor: string | null;
  avatarUrl: string | null;
  telegramEnabled?: boolean;
  telegramUsername?: string | null;
  telegramLinked?: boolean;
}

interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

export interface RegistrationSettings {
  codeRequired: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'bs_token';
  private readonly USER_KEY  = 'bs_user';

  currentUser = signal<AuthUser | null>(this.loadUser());

  constructor(private http: HttpClient, private router: Router) {
    this.normalizeStoredSession();
  }

  login(username: string, password: string) {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/login`, { username, password }).pipe(
      tap((res) => this.persist(res)),
    );
  }

  register(payload: {
    username: string;
    password: string;
    captchaToken: string;
    registrationCode?: string;
    avatarColor?: string;
  }) {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/register`, payload).pipe(
      tap((res) => this.persist(res)),
    );
  }

  getRegistrationSettings() {
    return this.http.post<RegistrationSettings>(`${environment.apiUrl}/auth/register/settings`, {});
  }

  logout() {
    this.clearSession();
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken() && !!this.currentUser();
  }

  handleUnauthorized() {
    if (!this.getToken() && !this.currentUser()) return;
    this.clearSession();
    this.router.navigate(['/login']);
  }

  updateCurrentUser(patch: Partial<AuthUser>) {
    const u = this.currentUser();
    if (!u) return;
    const updated = { ...u, ...patch };
    localStorage.setItem(this.USER_KEY, JSON.stringify(updated));
    this.currentUser.set(updated);
  }

  private persist(res: AuthResponse) {
    localStorage.setItem(this.TOKEN_KEY, res.accessToken);
    localStorage.setItem(this.USER_KEY, JSON.stringify(res.user));
    this.currentUser.set(res.user);
  }

  private loadUser(): AuthUser | null {
    const raw = localStorage.getItem(this.USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      localStorage.removeItem(this.USER_KEY);
      return null;
    }
  }

  private normalizeStoredSession() {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const user = this.currentUser();
    if (!token || !user) {
      this.clearSession();
    }
  }

  private clearSession() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.currentUser.set(null);
  }
}
